import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  BedDouble, Bath, Sofa, Home as HomeIcon, Calendar, Zap,
  ChevronLeft, ChevronRight, Check, Loader2, Phone, Mail, MapPin,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { PROPERTIES, CONTACT } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES_LIST = [
  "Double glazing throughout",
  "Fully-fitted modern kitchen",
  "Excellent transport links",
  "Close to local shops & cafes",
  "Wood flooring",
  "Great natural light",
];

function Gallery({ property }) {
  const imgs = [property.image, ...(property.gallery || [])];
  const [active, setActive] = useState(0);
  const go = (d) => setActive((a) => (a + d + imgs.length) % imgs.length);

  return (
    <div>
      <div className="relative overflow-hidden rounded-sm bg-gray-100 shadow-md">
        <img
          src={imgs[active]}
          onError={(e) => (e.currentTarget.src = property.fallback)}
          alt={property.address}
          className="h-[280px] w-full object-cover sm:h-[440px] lg:h-[520px]"
        />
        <span className="absolute left-0 top-5 bg-[#16323f] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white">
          {property.tag}
        </span>
        <button
          onClick={() => go(-1)}
          aria-label="Previous"
          className="absolute left-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#16323f] shadow transition hover:bg-white"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={() => go(1)}
          aria-label="Next"
          className="absolute right-4 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#16323f] shadow transition hover:bg-white"
        >
          <ChevronRight size={22} />
        </button>
        <span className="absolute bottom-4 right-4 rounded-sm bg-black/60 px-3 py-1 text-xs text-white">
          {active + 1} / {imgs.length}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-3">
        {imgs.slice(0, 5).map((src, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`overflow-hidden rounded-sm border-2 transition ${
              active === i ? "border-[#ec008c]" : "border-transparent opacity-80 hover:opacity-100"
            }`}
          >
            <img
              src={src}
              onError={(e) => (e.currentTarget.src = property.fallback)}
              alt=""
              className="h-16 w-full object-cover sm:h-20"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function ViewingForm({ property }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Missing details", description: "Please add your name, email and phone.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/viewings`, {
        ...form,
        property_id: property.id,
        property_title: property.address,
      });
      setDone(true);
      toast({ title: "Viewing requested", description: "We'll confirm your viewing shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-sm bg-white p-8 text-center shadow-lg">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#35a4a4] text-white">
          <Check size={28} />
        </div>
        <h3 className="mt-4 font-display text-xl font-600 text-[#16323f]">Viewing requested</h3>
        <p className="mt-2 text-sm text-gray-600">
          Thanks! The New Space team will be in touch to confirm a time to view this property.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-sm bg-white p-6 shadow-lg">
      <h3 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Book a viewing</h3>
      <p className="mt-1 text-sm text-gray-500">Arrange a viewing for this property.</p>
      <div className="mt-5 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="vf-name">Full name *</Label>
          <Input id="vf-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-email">Email *</Label>
          <Input id="vf-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-phone">Phone *</Label>
          <Input id="vf-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="07123 456789" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-date">Preferred date</Label>
          <Input id="vf-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="vf-msg">Message</Label>
          <Textarea id="vf-msg" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything we should know?" />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="group flex w-full items-center justify-center gap-2 bg-[#ec008c] py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70"
        >
          {loading ? (<><Loader2 size={16} className="animate-spin" /> Sending...</>) : (
            <>Request viewing <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></>
          )}
        </button>
      </div>
      <div className="mt-5 space-y-2 border-t border-gray-100 pt-5 text-sm text-gray-600">
        <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#ec008c]">
          <Phone size={16} className="text-[#35a4a4]" /> {CONTACT.phone}
        </a>
        <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-[#ec008c]">
          <Mail size={16} className="text-[#35a4a4]" /> {CONTACT.email}
        </a>
      </div>
    </form>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-sm bg-[#f4f6f7] p-4">
      <Icon size={22} className="text-[#35a4a4]" />
      <div>
        <p className="text-xs uppercase tracking-wider text-gray-400">{label}</p>
        <p className="font-semibold text-[#16323f]">{value}</p>
      </div>
    </div>
  );
}

function Content() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(() => PROPERTIES.find((p) => p.id === id) || null);
  const [loading, setLoading] = useState(!property);

  useEffect(() => {
    let alive = true;
    setProperty(PROPERTIES.find((p) => p.id === id) || null);
    axios
      .get(`${API}/properties/${id}`)
      .then((res) => {
        if (alive && res.data && res.data.id) setProperty(res.data);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  const crumbs = useMemo(
    () => [{ label: "Home", to: "/" }, { label: "Properties", to: "/#tenants" }, { label: property?.address }],
    [property]
  );
  const offerUrl = property
    ? `/make-an-offer?property=${encodeURIComponent(property.address)}&rent=${encodeURIComponent(property.price)}&pid=${encodeURIComponent(property.id || "")}`
    : "#";

  if (loading && !property) {
    return (
      <div className="App">
        <Header />
        <div className="grid min-h-[60vh] place-items-center pt-40 pb-24" data-testid="property-loading">
          <Loader2 size={40} className="animate-spin text-[#35a4a4]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="App">
        <Header />
        <div className="mx-auto max-w-[900px] px-5 pt-40 pb-24 text-center">
          <h1 className="font-display text-3xl font-600 text-[#16323f]">Property not found</h1>
          <button onClick={() => navigate("/")} className="mt-6 bg-[#16323f] px-8 py-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#ec008c]">
            Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Seo title={`${property.address} | To Rent — New Space, Hackney`} description={`${property.beds === 0 ? "Studio" : property.beds + " bedroom"} ${property.type.toLowerCase()} to rent at ${property.price}. ${property.desc.slice(0, 120)}`} path={`/property/${property.id}`} />
      <Header />
      <div className="pt-24 lg:pt-28">
        <div className="mx-auto max-w-[1200px] px-5 py-6">
          <Breadcrumbs
            variant="dark"
            items={crumbs}
          />
          <Link to="/#tenants" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#16323f] hover:text-[#ec008c]">
            <ChevronLeft size={16} /> Back to properties
          </Link>
        </div>

        <div className="mx-auto max-w-[1200px] px-5 pb-16">
          <Gallery property={property} />

          <div className="mt-10 grid gap-10 lg:grid-cols-3">
            {/* Main details */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="font-display text-3xl font-600 text-[#16323f] lg:text-4xl">{property.title}</h1>
                  <p className="mt-2 flex items-center gap-2 text-gray-500">
                    <MapPin size={18} className="text-[#35a4a4]" /> {property.address}
                  </p>
                </div>
                <p className="font-display text-3xl font-700 text-[#ec008c]">{property.price}</p>
              </div>

              <a
                href={offerUrl}
                data-testid="make-offer-btn"
                className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] px-8 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-[#ec008c]/25 transition-colors hover:bg-[#c60074] sm:w-auto"
              >
                Make an Offer
                <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>

              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
                <Stat icon={BedDouble} label="Bedrooms" value={property.beds === 0 ? "Studio" : property.beds} />
                <Stat icon={Bath} label="Bathrooms" value={property.baths} />
                <Stat icon={Sofa} label="Receptions" value={property.receptions} />
                <Stat icon={HomeIcon} label="Type" value={`${property.type} \u00b7 ${property.furnished}`} />
                <Stat icon={Calendar} label="Available" value={property.available} />
                <Stat icon={Zap} label="EPC Rating" value={property.epc} />
              </div>

              <div className="mt-10">
                <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Description</h2>
                <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-gray-600">
                  {(Array.isArray(property.fullDesc) ? property.fullDesc : [property.fullDesc || property.description || property.desc]).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>

              <div className="mt-10">
                <h2 className="font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">Key features</h2>
                <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                  {FEATURES_LIST.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-[15px] text-gray-700">
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#35a4a4] text-white">
                        <Check size={14} strokeWidth={3} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 rounded-sm border border-gray-200 p-5 text-xs leading-relaxed text-gray-500">
                Deposit: {property.deposit} (5 weeks' rent). Holding deposit: 1 week's rent. All tenancies are
                subject to referencing and contract. Please ask a member of the team for our full tenant fees.
              </div>
            </div>

            {/* Sticky viewing form */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28">
                <ViewingForm property={property} />
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
      <Toaster />
    </div>
  );
}

export default function PropertyDetail() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
